import * as React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import withWidth from '@material-ui/core/withWidth';
import clsx from 'clsx';
import AmountPrice from '@configuratorware/configurator-frontendgui/App/Screens/Configurator/Containers/AmountPrice';
import { selectOption as coreSelectOption } from '@configuratorware/configurator-frontendgui/App/Reducers/Configurator/Actions';
import { OptionClassificationIdentifiers, SIDEBAR_WIDTH } from '../Enums';
import DesignSection from '../Components/Sidebar/DesignSection';
import ComponentOptionsSection from '../Components/Sidebar/ComponentOptionsSection';
import SectionTitle from '../Components/Sidebar/SectionTitle';
import TextItemCard from '../Components/Sidebar/TextItemCard';
import ImageItemCard from '../Components/Sidebar/ImageItemCard';
import MountingTypeSection from '../Components/Sidebar/MountingTypeSection';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import BackgroundPanel from '../Components/Editor/BackgroundPanel';
import {
  getEditorObjects,
  getOptionClassificationById,
  getOptionsForClassification,
  getSelectedOptionForClassification,
} from '../Reducers/Configurator/Selectors';
import { setEditorSelectedId } from '../Reducers/Configurator/Actions';
import { useDeselectEditorOnClickAway } from '../Components/Editor/useDeselectEditorOnClickAway';
import { XXL_COLORS } from '../theme/colors';
import { SPACE_GROTESK_FONT_FAMILY } from '../theme/fonts';
import type { ImageItem, TextItem } from '../Reducers/Configurator/types';

const styles = (theme) => {
  const tokens = theme.xxlprint || XXL_COLORS;

  return {
    root: {
      width: SIDEBAR_WIDTH,
      flexShrink: 0,
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      backgroundColor: tokens.paperBackground,
      borderLeft: `1px solid ${tokens.borderStrong}`,
      pointerEvents: 'auto',
      // Mobile (<600px): full width, stacked under the canvas — the divider
      // moves from the left edge to the top edge (same border, repositioned).
      // Keep height:100% so the inner .scroll owns overflow and the footer stays pinned.
      [theme.breakpoints.down('xs')]: {
        width: '100%',
        borderLeft: 'none',
        borderTop: `1px solid ${tokens.borderStrong}`,
      },
    },
    scroll: {
      flex: 1,
      overflowY: 'auto',
      padding: theme.spacing(2),
    },
    elementsList: {
      marginTop: theme.spacing(2.5),
    },
    priceFooter: {
      flexShrink: 0,
      padding: theme.spacing(2),
      backgroundColor: tokens.paperBackground,
      color: tokens.sectionTitle,
      borderTop: `1px solid ${tokens.borderStrong}`,
      borderBottom: `1px solid ${tokens.borderStrong}`,
      // The AmountPrice card carries its own white surface — make it transparent
      // so the footer's own background shows through instead.
      '& .MuiCard-root, & .MuiPaper-root': {
        backgroundColor: 'transparent !important',
        boxShadow: 'none !important',
        border: 'none !important',
      },
      // Price text and labels rendered by AmountPrice match the footer's dark-on-light text color.
      '& .MuiTypography-root, & p, & span, & h1, & h2, & h3, & h4, & h5, & h6': {
        color: tokens.sectionTitle,
      },
      // fullWidth + flush container edge clips the hover shadow's right offset; reserve headroom (6px).
      '& [class*="MainButton-root"]': {
        maxWidth: 'calc(100% - 6px)',
      },
      // "1 STÜCK" quantity control — same color scheme + hover interaction as
      // the MainButton/CTA: flat black by default, cyan fill + back-shadow on hover.
      '& .MuiButton-text': {
        backgroundColor: `${tokens.primary} !important`,
        color: `${tokens.primaryContrast} !important`,
        fontFamily: SPACE_GROTESK_FONT_FAMILY,
        borderRadius: 0,
        border: `1px solid ${tokens.primary}`,
        boxShadow: 'none',
        textTransform: 'uppercase',
        fontWeight: 700,
        letterSpacing: '0.04em',
        padding: theme.spacing(1, 2),
        minWidth: 'auto',
        '&:hover': {
          backgroundColor: `${tokens.accent} !important`,
          boxShadow: '4px 4px 0 rgba(12,12,12,0.9) !important',
        },
        '& .MuiButton-label': {
          color: `${tokens.primaryContrast} !important`,
          boxShadow: 'none !important',
        },
        '&:hover .MuiButton-label': {
          color: `${tokens.primary} !important`,
        },
      },
      [theme.breakpoints.down('xs')]: {
        // Extra bottom padding so the CTA backshadow (4px offset) isn't clipped by the footer edge.
        padding: theme.spacing(2.5, 2, 2.75),
      },
    },
  };
};

function EditorSidebar({ classes }) {
  const dispatch = useDispatch();
  const objects = useSelector(getEditorObjects);
  const showAddToCartButton = useSelector(
    (state) => state.configurator?.configuration?.showAddToCartButton !== false
  );

  const materialOptions = useSelector(getOptionsForClassification(OptionClassificationIdentifiers.material));
  const materialSelected = useSelector(
    getSelectedOptionForClassification(OptionClassificationIdentifiers.material)
  );
  const materialClassification = useSelector(
    getOptionClassificationById(OptionClassificationIdentifiers.material)
  );

  // Auto-select material when only one option is available
  React.useEffect(() => {
    if (materialOptions.length === 1 && !materialSelected?.identifier && materialClassification) {
      dispatch(coreSelectOption({ ...materialOptions[0], amount: 1 }, true, false, materialClassification));
    }
  }, [materialOptions.length, !!materialSelected?.identifier]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasMaterial = materialOptions.length === 0 || !!materialSelected?.identifier;

  const items = Object.values(objects);
  const hasAutoSelected = React.useRef(false);

  React.useEffect(() => {
    if (hasAutoSelected.current) return;
    const firstId = Object.keys(objects)[0];
    if (!firstId) return;
    hasAutoSelected.current = true;
    dispatch(setEditorSelectedId(firstId));
  }, [items.length]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={classes.root} data-editor-sidebar>
      <div className={classes.scroll}>
        <ComponentOptionsSection classificationId={OptionClassificationIdentifiers.material} />
        <MountingTypeSection />
        {hasMaterial && (
          <>
            <DesignSection />
            {items.length > 0 && (
              <div className={classes.elementsList}>
                <SectionTitle>{t('elementsSection.title')}</SectionTitle>
                {items.map((item) =>
                  item.type === 'text' ? (
                    <TextItemCard key={item.id} item={item as TextItem} />
                  ) : (
                    <ImageItemCard key={item.id} item={item as ImageItem} />
                  )
                )}
              </div>
            )}
          </>
        )}
      </div>
      {showAddToCartButton !== false && (
        <div className={classes.priceFooter}>
          <AmountPrice boxed={false} />
        </div>
      )}
    </div>
  );
}

EditorSidebar.propTypes = {
  classes: PropTypes.object,
};

const SidebarWithStyles = withStyles(styles)(EditorSidebar);

const stylesLayout = (theme) => {
  const tokens = theme.xxlprint || XXL_COLORS;

  return {
    root: {
      display: 'flex',
      flexDirection: 'column',
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
      overflow: 'hidden',
      backgroundColor: tokens.pageBackground,
      // Core renders CreatorAlignTarget for 3D camera alignment; we use SvgDesignCanvas
      // instead, so neutralize it at all breakpoints. 
      '& [class*="CreatorAlignTarget"]': {
        display: 'none',
        margin: 0,
        maxHeight: 'none',
      },
    },
    header: {
      zIndex: 1,
      flexShrink: 0,
    },
    body: {
      flex: 1,
      display: 'flex',
      flexDirection: 'row',
      position: 'relative',
      minHeight: 0,
      // Mobile (<600px): stack canvas over controls in one column.
      [theme.breakpoints.down('xs')]: {
        flexDirection: 'column',
      },
    },
    canvasArea: {
      flex: 1,
      position: 'relative',
      minWidth: 0,
      backgroundColor: tokens.canvasBackground,
      // Mobile: canvas occupies a fixed top portion and stays put while the
      // controls below scroll (controls own their own scroll container).
      [theme.breakpoints.down('xs')]: {
        flex: '0 0 40%',
        width: '100%',
        minHeight: 0,
      },
    },
    visualization: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      pointerEvents: 'auto',
    },
    sidebar: {
      flexShrink: 0,
      height: '100%',
      zIndex: 2,
      // Mobile: full width, takes the remaining height below the canvas.
      [theme.breakpoints.down('xs')]: {
        width: '100%',
        height: 'auto',
        flex: '1 1 0',
        minHeight: 0,
      },
    },
  };
};

const XxlPrintLayout = ({
  classes,
  renderHeader,
  renderMain,
  renderVisualization,
  renderLoader,
  renderLicenseNotice,
  renderInvalidConfigurationNotice,
  renderInForeground,
}) => {
  useDeselectEditorOnClickAway();

  const content = renderInForeground?.() || (
    <>
      {renderLicenseNotice?.({ mobileOffset: 0 })}
      {renderInvalidConfigurationNotice?.()}
      <div className={classes.header}>{renderHeader?.({ area: 'header', classes })}</div>
      <div className={classes.body}>
        <div className={classes.canvasArea}>
          <div className={classes.visualization}>{renderVisualization?.()}</div>
          <BackgroundPanel />
        </div>
        <div className={classes.sidebar}>
          <SidebarWithStyles />
        </div>
      </div>
    </>
  );

  return (
    <div className={clsx(classes.root)}>
      {content}
      {renderMain?.()}
      {renderLoader?.()}
    </div>
  );
};

XxlPrintLayout.propTypes = {
  classes: PropTypes.object,
  renderHeader: PropTypes.func,
  renderMain: PropTypes.func,
  renderVisualization: PropTypes.func,
  renderLoader: PropTypes.func,
  renderLicenseNotice: PropTypes.func,
  renderInvalidConfigurationNotice: PropTypes.func,
  renderInForeground: PropTypes.func,
};

export default withWidth()(withStyles(stylesLayout)(XxlPrintLayout));
